function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
function _slicedToArray(r, e) { return _arrayWithHoles(r) || _iterableToArrayLimit(r, e) || _unsupportedIterableToArray(r, e) || _nonIterableRest(); }
function _nonIterableRest() { throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }
function _unsupportedIterableToArray(r, a) { if (r) { if ("string" == typeof r) return _arrayLikeToArray(r, a); var t = {}.toString.call(r).slice(8, -1); return "Object" === t && r.constructor && (t = r.constructor.name), "Map" === t || "Set" === t ? Array.from(r) : "Arguments" === t || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(t) ? _arrayLikeToArray(r, a) : void 0; } }
function _arrayLikeToArray(r, a) { (null == a || a > r.length) && (a = r.length); for (var e = 0, n = Array(a); e < a; e++) n[e] = r[e]; return n; }
function _iterableToArrayLimit(r, l) { var t = null == r ? null : "undefined" != typeof Symbol && r[Symbol.iterator] || r["@@iterator"]; if (null != t) { var e, n, i, u, a = [], f = !0, o = !1; try { if (i = (t = t.call(r)).next, 0 === l) { if (Object(t) !== t) return; f = !1; } else for (; !(f = (e = i.call(t)).done) && (a.push(e.value), a.length !== l); f = !0); } catch (r) { o = !0, n = r; } finally { try { if (!f && null != t["return"] && (u = t["return"](), Object(u) !== u)) return; } finally { if (o) throw n; } } return a; } }
function _arrayWithHoles(r) { if (Array.isArray(r)) return r; }
/** *****************************************************************************
 * Licensed Materials - Property of IBM
 * (c) Copyright IBM Corporation 2023, 2025. All Rights Reserved.
 *
 * Note to U.S. Government Users Restricted Rights:
 * Use, duplication or disclosure restricted by GSA ADP Schedule
 * Contract with IBM Corp.
 ****************************************************************************** */
import React, { useContext, useEffect, useState, useRef } from "react";
import { SkeletonText, Tag, Tooltip } from "@carbon/react";
import { WarningAlt } from "@carbon/react/icons";
import Semaphore from "../../utils/semaphore";
import vaultUtil from "../../utils/vault-util";
import { xhrPromise } from "../../utils/xhr";
import { i18nContext, idContext } from "../PropertyTable/table-context";
var CANONICAL = "canonical";
var CRN = "crn";
var REF = "ref-sm";
var DETAILS_URL = "".concat(vaultUtil.SECRETS_API_PATH, "/details");

// Semaphore for allowing X simultaneous requests
var sem = new Semaphore(3);
function SecretReference(_ref) {
  var reference = _ref.reference,
    externalWarning = _ref.warning;
  // State
  var _useState = useState(""),
    _useState2 = _slicedToArray(_useState, 2),
    label = _useState2[0],
    setLabel = _useState2[1];
  var _useState3 = useState("purple"),
    _useState4 = _slicedToArray(_useState3, 2),
    color = _useState4[0],
    setColor = _useState4[1];
  var _useState5 = useState(CANONICAL),
    _useState6 = _slicedToArray(_useState5, 2),
    type = _useState6[0],
    setType = _useState6[1];
  var _useState7 = useState(""),
    _useState8 = _slicedToArray(_useState7, 2),
    href = _useState8[0],
    setHref = _useState8[1];
  var _useState9 = useState(""),
    _useState0 = _slicedToArray(_useState9, 2),
    fetchedName = _useState0[0],
    setFetchedName = _useState0[1];
  var _useState1 = useState(false),
    _useState10 = _slicedToArray(_useState1, 2),
    fetching = _useState10[0],
    setFetching = _useState10[1];
  var _useState11 = useState(""),
    _useState12 = _slicedToArray(_useState11, 2),
    warning = _useState12[0],
    setWarning = _useState12[1];

  // Refs to handle xhr requests
  var cancelXhr = useRef({});

  // Context vars
  var _useContext = useContext(idContext),
    envId = _useContext.envId,
    accountContext = _useContext.accountContext,
    bssAccount = _useContext.bssAccount,
    toolchainId = _useContext.toolchainId;
  var i18n = useContext(i18nContext);
  var handleFetchError = function handleFetchError() {
    setFetching(false);
    setWarning(i18n.messages.secretDetailsError);
  };
  var handleFetchComplete = function handleFetchComplete(_ref2) {
    var name = _ref2.name;
    setFetching(false);
    setFetchedName(name);
  };
  var startFetch = function startFetch() {
    // Setup the cancel object
    var cancelObj = {
      cancel: false
    };
    cancelXhr.current = cancelObj;

    // Start the fetch
    sem.acquire().then(function () {
      if (cancelObj.cancel === false) {
        setFetching(true);
        var url = "".concat(DETAILS_URL, "?reference=").concat(encodeURIComponent(reference));
        return xhrPromise(url, {
          dataType: "json",
          method: "GET",
          env_id: envId,
          accountContext: accountContext,
          bssAccount: bssAccount,
          toolchainId: toolchainId
        }).then(function (result) {
          if (cancelObj.cancel === false) {
            handleFetchComplete(result.body);
          }
        })["catch"](function (error) {
          if (cancelObj.cancel === false) {
            handleFetchError(error);
          }
        });
      }
    })["finally"](function () {
      sem.release();
    });
  };
  var stopFetch = function stopFetch() {
    // Mark the current xhr as cancelled
    cancelXhr.current.cancel = true;
    setFetching(false);
  };
  useEffect(function () {
    return function () {
      // Cleanup - cancel the xhr if any
      cancelXhr.current.cancel = true;
    };
  }, []);
  useEffect(function () {
    var _ref3 = vaultUtil.parseValue(reference) || {},
      _ref3$group = _ref3.group,
      group = _ref3$group === void 0 ? "" : _ref3$group,
      _ref3$secret = _ref3.secret,
      secret = _ref3$secret === void 0 ? "" : _ref3$secret,
      _ref3$field = _ref3.field,
      field = _ref3$field === void 0 ? "" : _ref3$field,
      _ref3$provider = _ref3.provider,
      provider = _ref3$provider === void 0 ? "" : _ref3$provider,
      _ref3$instanceUrl = _ref3.instanceUrl,
      instanceUrl = _ref3$instanceUrl === void 0 ? "" : _ref3$instanceUrl;
    setLabel((group ? "".concat(group, ".").concat(secret) : secret) + (field ? ".".concat(field) : ""));
    setType(provider === vaultUtil.CRN_PROVIDER_TYPE ? CRN : provider === vaultUtil.REF_SM_PROVIDER_TYPE ? REF : CANONICAL);
    setColor(provider === vaultUtil.CRN_PROVIDER_TYPE ? "blue" : provider === vaultUtil.REF_SM_PROVIDER_TYPE ? "teal" : "purple");
    setHref(instanceUrl);
    setFetchedName("");
    setWarning("");
    stopFetch();
    if (provider === vaultUtil.CRN_PROVIDER_TYPE && toolchainId) {
      startFetch();
    }
  }, [reference]);
  var handleTagClick = function handleTagClick(evt) {
    evt.preventDefault();
    window.open(href, "_blank");
  };
  var renderWarning = function renderWarning() {
    if (!warning && !externalWarning) {
      return null;
    }
    return /*#__PURE__*/React.createElement("div", {
      className: "ocr--warning-div"
    }, /*#__PURE__*/React.createElement(Tooltip, {
      align: "top-left",
      className: "icon--warning",
      label: externalWarning || warning
    }, /*#__PURE__*/React.createElement("button", {
      className: "ocr--tooltip-trigger",
      type: "button"
    }, /*#__PURE__*/React.createElement(WarningAlt, null))));
  };
  return /*#__PURE__*/React.createElement("div", {
    className: "ocr--secret-reference"
  }, fetching ? /*#__PURE__*/React.createElement(SkeletonText, {
    width: "50%"
  }) : /*#__PURE__*/React.createElement(Tag, _extends({
    className: "ocr--tag",
    type: color
  }, type === CRN ? {
    onClick: handleTagClick
  } : {}), fetchedName || label), renderWarning());
}
export default SecretReference;